import {
  Building2,
  Building,
  MapPin,
  Phone,
  CheckCircle,
  AlertTriangle,
  Landmark,
} from "lucide-react";

export interface BillingAddress {
  company?: string | null;
  name?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  phone?: string | null;
  taxId?: string | null;
  iban?: string | null;
}

interface BillingAddressCardProps {
  address: BillingAddress;
  label: string;
  role?: "seller" | "buyer";
  variant?: "card" | "admin" | "compact";
  emptyText?: string;
  className?: string;
}

/**
 * Shared billing address display with three visual variants:
 * - "card": Read-only summary with Building2 icon + checkmark (ContractReviewStep)
 * - "admin": Admin view with Vorhanden/Ausstehend status badge (AdminStepContract)
 * - "compact": Compact inline with Building/MapPin/Phone icons (AdminIdentityPanel)
 */
export function BillingAddressCard({
  address,
  label,
  role,
  variant = "card",
  emptyText = "Noch nicht ausgefüllt",
  className = "",
}: BillingAddressCardProps) {
  const hasAddress = !!address.name || !!address.addressLine1;
  const showTaxId = role === "seller";

  if (variant === "compact") {
    return (
      <CompactVariant
        address={address}
        label={label}
        showTaxId={showTaxId}
        emptyText={emptyText}
        className={className}
      />
    );
  }

  if (variant === "admin") {
    return (
      <AdminVariant
        address={address}
        label={label}
        showTaxId={showTaxId}
        hasAddress={hasAddress}
        emptyText={emptyText}
        className={className}
      />
    );
  }

  // Default: "card" variant
  return (
    <CardVariant address={address} label={label} showTaxId={showTaxId} className={className} />
  );
}

// ─── Card variant (ContractReviewStep) ───────────────────────────────────────

function CardVariant({
  address,
  label,
  showTaxId,
  className,
}: {
  address: BillingAddress;
  label: string;
  showTaxId: boolean;
  className: string;
}) {
  return (
    <div className={`rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-zinc-400" />
        <p className="text-sm font-medium text-zinc-700">Vertragsadresse {label}</p>
        <CheckCircle className="w-4 h-4 text-emerald-500 ml-auto" />
      </div>
      <AddressLines address={address} showTaxId={showTaxId} textClass="text-sm text-zinc-600" />
    </div>
  );
}

// ─── Admin variant (AdminStepContract) ───────────────────────────────────────

function AdminVariant({
  address,
  label,
  showTaxId,
  hasAddress,
  emptyText,
  className,
}: {
  address: BillingAddress;
  label: string;
  showTaxId: boolean;
  hasAddress: boolean;
  emptyText: string;
  className: string;
}) {
  return (
    <div className={`p-4 bg-zinc-50 rounded-lg ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{label}</p>
        {hasAddress ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-3 h-3" /> Vorhanden
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <AlertTriangle className="w-3 h-3" /> Ausstehend
          </span>
        )}
      </div>
      {hasAddress ? (
        <AddressLines address={address} showTaxId={showTaxId} textClass="text-sm text-zinc-700" />
      ) : (
        <p className="text-sm text-zinc-400">{emptyText}</p>
      )}
    </div>
  );
}

// ─── Compact variant (AdminIdentityPanel) ────────────────────────────────────

function CompactVariant({
  address,
  label,
  showTaxId,
  emptyText,
  className,
}: {
  address: BillingAddress;
  label: string;
  showTaxId: boolean;
  emptyText: string;
  className: string;
}) {
  if (!address.name && !address.addressLine1) {
    return (
      <div
        className={`ml-8 mt-1 mb-2 px-3 py-2 bg-zinc-50 rounded text-xs text-zinc-400 ${className}`}
      >
        {label}: {emptyText}
      </div>
    );
  }

  return (
    <div className={`ml-8 mt-1 mb-2 px-3 py-2 bg-zinc-50 rounded ${className}`}>
      <div className="grid grid-cols-1 gap-1 text-xs text-zinc-600">
        {address.company && (
          <div className="flex items-center gap-1.5">
            <Building className="w-3 h-3 text-zinc-400" />
            <span className="font-medium">{address.company}</span>
          </div>
        )}
        {address.name && <div className="ml-[18px]">{address.name}</div>}
        {address.addressLine1 && (
          <div className="flex items-start gap-1.5">
            <MapPin className="w-3 h-3 text-zinc-400 mt-0.5" />
            <div>
              <div>{address.addressLine1}</div>
              {address.addressLine2 && <div>{address.addressLine2}</div>}
              {address.postalCode && address.city && (
                <div>
                  {address.postalCode} {address.city}
                </div>
              )}
            </div>
          </div>
        )}
        {address.phone && (
          <div className="flex items-center gap-1.5">
            <Phone className="w-3 h-3 text-zinc-400" />
            <span>{address.phone}</span>
          </div>
        )}
        {showTaxId && address.taxId && (
          <div className="ml-[18px] text-zinc-500">Steuernummer: {address.taxId}</div>
        )}
        {showTaxId && address.iban && (
          <div className="flex items-center gap-1.5">
            <Landmark className="w-3 h-3 text-zinc-400" />
            <span className="text-zinc-500">IBAN: {address.iban}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared address lines (card + admin variants) ────────────────────────────

function AddressLines({
  address,
  showTaxId,
  textClass,
}: {
  address: BillingAddress;
  showTaxId: boolean;
  textClass: string;
}) {
  return (
    <div className={`${textClass} space-y-0.5`}>
      {address.company && <p className="font-medium">{address.company}</p>}
      {address.name && <p>{address.name}</p>}
      {address.addressLine1 && <p>{address.addressLine1}</p>}
      {address.addressLine2 && <p>{address.addressLine2}</p>}
      {(address.postalCode || address.city) && (
        <p>{[address.postalCode, address.city].filter(Boolean).join(" ")}</p>
      )}
      {showTaxId && address.taxId && (
        <p className="text-zinc-400 mt-1">Steuernummer: {address.taxId}</p>
      )}
      {showTaxId && address.iban && <p className="text-zinc-400 mt-1">IBAN: {address.iban}</p>}
    </div>
  );
}
